/**
 * Shared modal-dialog styling (operator 2026-07-08 — modern refresh).
 *
 * A single source of truth for the create/assign dialogs (`NewCaseDialog`,
 * `AssignCaseDialog`, …) so they stay visually coherent: a frosted backdrop, a
 * soft-elevated panel, an UPPERCASE tracked title over a divider, airy
 * rounded-xl fields with a gold focus ring, and a prominent primary action.
 */
import { cn } from "@/lib/utils";

/** Full-screen frosted backdrop that centers the panel. */
export const dialogOverlayCls =
  "fixed inset-0 z-50 flex items-center justify-center bg-coffee-950/50 p-4 backdrop-blur-sm dark:bg-coffee-950/70";

/** The elevated dialog card. */
export const dialogPanelCls =
  "max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-cream-200/80 bg-white p-6 shadow-2xl shadow-coffee-900/25 dark:border-coffee-700/60 dark:bg-coffee-900";

/** Header row (title + close), separated from the body by a hairline divider. */
export const dialogHeaderCls =
  "mb-5 flex items-center justify-between gap-2 border-b border-cream-200/70 pb-3 dark:border-coffee-700/50";

/** UPPERCASE, letter-spaced title. */
export const dialogTitleCls =
  "text-body font-semibold uppercase tracking-[0.12em] text-coffee-800 dark:text-cream-100";

/** Close (X) button. */
export const dialogCloseBtnCls =
  "shrink-0 rounded-lg p-1 text-cream-500 transition-colors hover:bg-cream-100/70 hover:text-coffee-700 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200";

/** Field label — small uppercase caption. */
export const dialogLabelCls =
  "mb-1.5 block text-caption font-medium uppercase tracking-wide text-cream-500";

/** Text input / select field. */
export const dialogInputCls =
  "w-full rounded-xl border border-cream-200/80 bg-cream-50/60 px-3 py-2.5 text-body-sm text-coffee-900 transition-colors placeholder:text-cream-500 focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/40 dark:border-coffee-700/60 dark:bg-coffee-800/40 dark:text-cream-100 dark:placeholder:text-coffee-500 dark:focus:bg-coffee-800/70";

/** Footer action row (right-aligned). */
export const dialogFooterCls = "mt-6 flex items-center justify-end gap-2";

/** Secondary (Cancel) button. */
export const dialogCancelBtnCls =
  "rounded-lg px-4 py-2 text-body-sm font-medium text-cream-500 transition-colors hover:bg-cream-100/70 disabled:opacity-50 dark:text-cream-400 dark:hover:bg-coffee-800/60";

/** Primary (submit) button — pass `enabled` for the active gold treatment. */
export function dialogPrimaryBtnCls(enabled: boolean): string {
  return cn(
    "inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-body-sm font-semibold transition-colors disabled:cursor-not-allowed",
    enabled
      ? "bg-gold-500 text-coffee-950 shadow-sm shadow-gold-500/30 hover:bg-gold-400"
      : "bg-cream-200/70 text-cream-500 dark:bg-coffee-800 dark:text-coffee-500",
  );
}
